import { layoutCoordinate } from "../utils/displayDensity";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { csvParse } from "d3-dsv";
import { scaleLinear } from "d3-scale";
import { geoNaturalEarth1 } from "d3-geo";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import * as topojson from "topojson-client";
import * as pako from "pako";

/* ========= LIVE DATA ========= */
const METADATA_URL = "https://mindgamedataapi.s3.us-east-1.amazonaws.com/metadata.csv.gz";
const TRENDS_URL   = "https://mindgamedataapi.s3.us-east-1.amazonaws.com/Country_Trended.csv.gz";
const WORLD_TOPO   = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-10m.json";

/* ========= fetch helpers ========= */
function useFetchArrayBuffer(url) {
  const [buf, setBuf] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!url) return;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const b = await r.arrayBuffer();
        if (alive) setBuf(b);
      } catch (e) {
        if (alive) setErr((e && e.message) || String(e));
      }
    })();
    return () => { alive = false; };
  }, [url]);
  return { buf, err };
}

function useFetchText(url) {
  const [text, setText] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!url) return;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const t = await r.text();
        if (alive) setText(t);
      } catch (e) {
        if (alive) setErr((e && e.message) || String(e));
      }
    })();
    return () => { alive = false; };
  }, [url]);
  return { text, err };
}

/* ========= parsers ========= */
function useMetadataGames(url) {
  const { buf, err } = useFetchArrayBuffer(url);
  const { games, gameSet, metaErr } = useMemo(() => {
    try {
      if (!buf) return { games: [], gameSet: new Set(), metaErr: null };
      const inflated = pako.ungzip(new Uint8Array(buf), { to: "string" });
      const rows = csvParse(inflated);
      const gs = Array.from(new Set(rows.map(r => (r["Game"] || "").toString().trim()).filter(Boolean))).sort();
      return { games: gs, gameSet: new Set(gs), metaErr: null };
    } catch (e) {
      return { games: [], gameSet: new Set(), metaErr: (e && e.message) || String(e) };
    }
  }, [buf]);
  return { games, gameSet, error: err || metaErr };
}

function useCountryTrends(url) {
  const { buf, err } = useFetchArrayBuffer(url);
  const { data, parseErr } = useMemo(() => {
    try {
      if (!buf) return { data: [], parseErr: null };
      const inflated = pako.ungzip(new Uint8Array(buf), { to: "string" });
      const rows = csvParse(inflated);
      const out = rows.map(r => ({
        Period:  (r["Period"]  || "").toString().trim(),
        Game:    (r["Game"]    || "").toString().trim(),
        Country: (r["Country"] || "").toString().trim(),
        Index:   Number(r["Index"]) || 0,
      }));
      return { data: out, parseErr: null };
    } catch (e) {
      return { data: [], parseErr: (e && e.message) || String(e) };
    }
  }, [buf]);
  return { data, error: err || parseErr };
}

function useWorldGeos(url) {
  const { text, err } = useFetchText(url);
  const { geos, geoErr } = useMemo(() => {
    try {
      if (!text) return { geos: null, geoErr: null };
      const topo = JSON.parse(text);
      const feat = topojson.feature(topo, topo.objects.countries);
      return { geos: feat.features, geoErr: null };
    } catch (e) {
      return { geos: null, geoErr: (e && e.message) || String(e) };
    }
  }, [text]);
  return { geos, error: err || geoErr };
}

/* ========= helpers ========= */
const COUNTRY_ALIASES = {
  "United States of America": "United States",
  "United States": "United States",
  "Russian Federation": "Russia",
  "Korea, Republic of": "South Korea",
  Korea: "South Korea",
  "Viet Nam": "Vietnam",
  Czechia: "Czech Republic",
  "Syrian Arab Republic": "Syria",
  "Lao People's Democratic Republic": "Laos",
  "Côte d’Ivoire": "Cote d'Ivoire",
  "Côte d'Ivoire": "Cote d'Ivoire",
  "Ivory Coast": "Cote d'Ivoire",
  "Democratic Republic of the Congo": "Congo - Kinshasa",
  "Republic of the Congo": "Congo - Brazzaville",
  Myanmar: "Burma",
  Eswatini: "Swaziland",
};
const normalizeCountryName = (n) => COUNTRY_ALIASES[n] || n;
const parseDateMaybe = (s) => { const d = new Date(s); return isNaN(d.getTime()) ? 0 : d.getTime(); };

/* === BRAND palette (low→high; blue = high; 1.0 = neutral) === */
const BRAND_COLORS = [
  "#ff5342", "#fe6d4e", "#ff9463", "#ffc28b", // < 1.0
  "#819898",                                  // = 1.0
  "#4c7d8e", "#174d7c", "#2794c3",            // > 1.0
];
function makeDivergingBrandScale(domainMin, domainMax) {
  const mid = 1.0;
  const d0 = Math.min(domainMin ?? 0.5, 0.5);
  const d2 = Math.max(domainMax ?? 2.0, 2.0);
  const N = BRAND_COLORS.length; // 8
  const domain = Array.from({ length: N }, (_, i) => d0 + (i * (d2 - d0)) / (N - 1));
  const midIdx = Math.floor(N / 2); // 4
  domain[midIdx] = mid; // hard anchor for 1.0
  return scaleLinear().domain(domain).range(BRAND_COLORS).clamp(true);
}
const brandLegendCSS = () =>
  `linear-gradient(90deg, ${BRAND_COLORS.map((c,i)=>`${c} ${(i/(BRAND_COLORS.length-1))*100}%`).join(", ")})`;

/* ========= Tiers ========= */
const TIER1 = [
  "China","United States","Japan","South Korea","Germany","United Kingdom","France",
  "Canada","Mexico","Brazil","Italy","Spain","Russia","India","Australia"
];
const TIER2 = [
  "Taiwan","Turkey","Saudi Arabia","Thailand","Indonesia","Poland","Netherlands","Sweden",
  "Belgium","Argentina","Vietnam","Philippines","United Arab Emirates","Malaysia","Denmark",
  "Norway","Austria","Switzerland","Singapore","Portugal","South Africa","New Zealand","Chile",
  "Colombia"
];

/* ========= page ========= */
export default function Country() {
  // We still fetch metadata (in case you need it later) but only use trends to populate the dropdown
  const { games: _metaGames } = useMetadataGames(METADATA_URL);
  const { data: trends } = useCountryTrends(TRENDS_URL);
  const { geos: worldGeos } = useWorldGeos(WORLD_TOPO);

  // Only show games that actually have Country rows
  const gamesWithCountry = useMemo(() => {
    const set = new Set(trends.map(d => d.Game).filter(Boolean));
    return Array.from(set).sort((a,b)=>a.localeCompare(b));
  }, [trends]);

  const [game, setGame] = useState("");
  const [autoPlay, setAutoPlay] = useState(true);
  const [periodIdx, setPeriodIdx] = useState(0);
  const [showTier2, setShowTier2] = useState(false);
  const [showTier3, setShowTier3] = useState(false);
  const timerRef = useRef(null);

  // tooltip state for map
  const mapWrapRef = useRef(null);
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, name: "", value: 0 });

  // Default select from the games that actually have country data
  useEffect(() => {
    if (!game && gamesWithCountry.length) setGame(gamesWithCountry[0]);
  }, [gamesWithCountry, game]);

  const { last4Periods, colorScale, groupedRows, mapValuesByPeriod } = useMemo(() => {
    const subset = trends.filter(d => d.Game === game);
    const periods = Array.from(new Set(subset.map(d => d.Period)))
      .sort((a,b) => parseDateMaybe(a) - parseDateMaybe(b));
    const last4 = periods.slice(-4);

    const byCountry = new Map();
    let minV = Infinity, maxV = -Infinity;
    for (const d of subset) {
      if (!last4.includes(d.Period)) continue;
      const c = d.Country;
      const obj = byCountry.get(c) || {};
      obj[d.Period] = d.Index;
      byCountry.set(c, obj);
      if (d.Index > 0) { minV = Math.min(minV, d.Index); maxV = Math.max(maxV, d.Index); }
    }

    // sort by latest period desc
    const latest = last4[last4.length - 1];
    const allCountriesSorted = Array.from(byCountry.keys())
      .sort((a,b) => (byCountry.get(b)?.[latest] || 0) - (byCountry.get(a)?.[latest] || 0));

    // group into tiers
    const inTier1 = new Set(TIER1);
    const inTier2 = new Set(TIER2);
    const rowsT1 = [];
    const rowsT2 = [];
    const rowsT3 = [];
    for (const c of allCountriesSorted) {
      const values = last4.map(p => byCountry.get(c)?.[p] ?? 0);
      if (inTier1.has(c)) rowsT1.push({ country: c, values });
      else if (inTier2.has(c)) rowsT2.push({ country: c, values });
      else rowsT3.push({ country: c, values });
    }
    const grouped = { t1: rowsT1, t2: rowsT2, t3: rowsT3 };

    // map values
    const mapByPeriod = {};
    last4.forEach(p => mapByPeriod[p] = {});
    for (const [c, obj] of byCountry.entries())
      for (const p of last4) mapByPeriod[p][c] = obj[p] ?? 0;

    const cs = makeDivergingBrandScale(
      Number.isFinite(minV) ? minV : 0.5,
      Number.isFinite(maxV) ? maxV : 2.0
    );
    return { last4Periods: last4, colorScale: cs, groupedRows: grouped, mapValuesByPeriod: mapByPeriod };
  }, [trends, game]);

  useEffect(() => {
    if (!autoPlay || last4Periods.length < 2) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setPeriodIdx(p => (p + 1) % Math.max(1, last4Periods.length)), 1800);
    return () => clearInterval(timerRef.current);
  }, [autoPlay, last4Periods]);

  const currentPeriod = last4Periods[periodIdx] || "";
  const currentMap = currentPeriod ? (mapValuesByPeriod[currentPeriod] || {}) : {};
  const valueToBg = (v) => ({ backgroundColor: colorScale(v) });

  // glow threshold = top 10% or >= 1.6
  const glowThreshold = useMemo(() => {
    const vals = Object.values(currentMap).filter(v => v > 0).sort((a,b) => a - b);
    if (!vals.length) return 1.6;
    const q = vals[Math.floor(vals.length * 0.9)];
    return Math.max(1.6, q);
  }, [currentMap]);

  // reusable tier section (inside one scroll container)
  const Section = ({ title, rows, collapsible, open, onToggle }) => (
    <div style={{ marginBottom: 8, border: "1px solid #191919", borderRadius: 8, overflow: "hidden" }}>
      <div
        onClick={collapsible ? onToggle : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 10px",
          background: "#101010",
          cursor: collapsible ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <span style={{ fontWeight: 800 }}>{title}</span>
        {collapsible && (
          <span style={{ color: "#9ab", fontSize: 12 }}>{open ? "Hide" : "Show"}</span>
        )}
      </div>
      {!collapsible || open ? (
        <div style={{ overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "auto" }} />
              {last4Periods.map((_, i) => (<col key={i} style={{ width: "80px" }} />))}
            </colgroup>
            <thead>
              <tr style={{ background: "#0f0f0f" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", position: "sticky", left: 0, background: "#0f0f0f", zIndex: 2 }}>Country</th>
                {last4Periods.map(p => (
                  <th key={p} style={{ textAlign: "left", padding: "6px 6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.country} style={{ borderTop: "1px solid #181818" }}>
                  <td style={{ padding: "4px 8px", position: "sticky", left: 0, background: "#0b0b0b", zIndex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.country}
                  </td>
                  {row.values.map((v, i) => (
                    <td key={i} style={{ padding: 2, textAlign: "left", ...valueToBg(v) }} title={`${last4Periods[i]} • ${row.country} • ${v.toFixed(2)}`}>
                      <span style={{ fontWeight: 700 }}>{v.toFixed(2)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      style={{
        height: "calc(var(--mg-vh) * 100)",
        background: "#000",
        color: "#fff",
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 10
      }}
    >
      {/* header (compact) */}
      <div style={{ display: "flex", gap: 16, alignItems: "center", padding: "10px 14px 0 14px" }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Country Index Dashboard</h1>
          <div style={{ color: "#aaa", fontSize: 12, marginTop: 2 }}>Tiered heatmap + animated world map for the last 4 periods.</div>
        </div>
        <select
          value={game}
          onChange={(e) => setGame(e.target.value)}
          style={{ background: "#111", color: "#fff", border: "1px solid #333", padding: "6px 8px", borderRadius: 6, minWidth: 260 }}
        >
          <option value="" disabled>Select Game…</option>
          {gamesWithCountry.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <button
          onClick={() => setAutoPlay(a => !a)}
          style={{ background: autoPlay ? "#222" : "#2794c3", border: "1px solid #444", color: "#fff", padding: "6px 10px", borderRadius: 6, fontWeight: 700, cursor: "pointer" }}
        >
          {autoPlay ? "⏸ Pause" : "▶ Play"}
        </button>
      </div>

      {/* content band: two fixed cards that fit viewport */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(520px, 0.58fr) 1fr",
          gap: 12,
          padding: "0 12px 12px 12px",
          overflow: "hidden"
        }}
      >
        {/* LEFT CARD: heatmap column with internal scroll */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: "auto 1fr",
            background: "#0b0b0b",
            border: "1px solid #222",
            borderRadius: 12,
            minWidth: 0,
            overflow: "hidden"
          }}
        >
          {/* mini legend / subtitle */}
          <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Heatmap (Last 4 Periods)</span>
            <span style={{ fontSize: 11, color: "#aaa" }}>Tier 1 visible; expand Tier 2/3 to explore</span>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#aaa", fontSize: 11 }}>Index</span>
              <div style={{ width: 160, height: 8, borderRadius: 6, background: brandLegendCSS(), boxShadow: "inset 0 0 0 1px #222" }} />
              <span style={{ color: "#aaa", fontSize: 11 }}>low • ≈1.0 • high</span>
            </div>
          </div>

          {/* single scroll container for all tiers */}
          <div style={{ overflow: "auto", padding: "0 10px 10px 10px" }}>
            <Section title="Tier 1 — Major Markets" rows={groupedRows.t1} collapsible={false} />
            <Section
              title="Tier 2 — Large / Growth Markets"
              rows={groupedRows.t2}
              collapsible
              open={showTier2}
              onToggle={() => setShowTier2(s => !s)}
            />
            <Section
              title="Tier 3 — Other Markets"
              rows={groupedRows.t3}
              collapsible
              open={showTier3}
              onToggle={() => setShowTier3(s => !s)}
            />
          </div>
        </div>

        {/* RIGHT CARD: world map, centered & anchored */}
        <div
          ref={mapWrapRef}
          style={{
            position: "relative",
            background: "transparent",
            border: "1px solid #222",
            borderRadius: 12,
            padding: 8,
            display: "grid",
            gridTemplateRows: "auto 1fr",
            minWidth: 0
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "4px 6px 0 6px" }}>
            <div style={{ fontWeight: 700 }}>World Map</div>
            <div style={{ color: "#aaa", fontSize: 12 }}>{last4Periods.length ? `Animating: ${last4Periods.join(" → ")}` : "—"}</div>
            <div style={{ marginLeft: "auto", color: "#ccc", fontSize: 12 }}>Period: <b>{currentPeriod || "—"}</b></div>
          </div>

          {/* aspect box keeps map centered and not “floating” */}
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            {worldGeos ? (
              <ComposableMap
                projection={geoNaturalEarth1()}
                width={1100}
                height={580}
                style={{ width: "96%", height: "96%", background: "transparent" }}
              >
                <defs>
                  <filter id="countryGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <Geographies geography={worldGeos}>
                  {({ geographies }) =>
                    geographies
                      .filter(geo => normalizeCountryName(geo.properties.name) !== "Antarctica")
                      .map(geo => {
                        const name = normalizeCountryName(geo.properties.name);
                        const v = currentMap?.[name] ?? 0;
                        const hasData = v > 0;
                        const fill = hasData ? colorScale(v) : "rgba(255,255,255,0.08)";
                        const isHigh = v >= glowThreshold;

                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={fill}
                            stroke="rgba(255,255,255,0.12)"
                            strokeWidth={isHigh ? 1.1 : 0.6}
                            style={{
                              default: { outline: "none", transition: "fill 400ms ease" },
                              hover:   { outline: "none" },
                              pressed: { outline: "none" }
                            }}
                            filter={isHigh ? "url(#countryGlow)" : undefined}
                            onMouseEnter={(e) => {
                              const rect = mapWrapRef.current?.getBoundingClientRect?.();
                              const x = (layoutCoordinate(e.clientX) - (rect?.left || 0));
                              const y = (layoutCoordinate(e.clientY) - (rect?.top || 0));
                              setTip({ show: true, x, y, name, value: v });
                            }}
                            onMouseMove={(e) => {
                              const rect = mapWrapRef.current?.getBoundingClientRect?.();
                              const x = (layoutCoordinate(e.clientX) - (rect?.left || 0));
                              const y = (layoutCoordinate(e.clientY) - (rect?.top || 0));
                              setTip(t => ({ ...t, x, y }));
                            }}
                            onMouseLeave={() => setTip(t => ({ ...t, show: false }))}
                          />
                        );
                      })
                  }
                </Geographies>
              </ComposableMap>
            ) : (
              <div style={{ color: "#888", fontSize: 13 }}>Loading world map…</div>
            )}

            {/* Tooltip */}
            {tip.show && (
              <div
                style={{
                  position: "absolute",
                  left: tip.x + 12,
                  top: tip.y + 12,
                  background: "rgba(20,20,20,0.95)",
                  border: "1px solid #333",
                  borderRadius: 8,
                  padding: "8px 10px",
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.35)"
                }}
              >
                <div style={{ color: "#9cc", fontWeight: 700, marginBottom: 2 }}>{tip.name}</div>
                <div style={{ color: "#ddd" }}>
                  {currentPeriod && (<span style={{ color: "#aaa" }}>{currentPeriod}: </span>)}
                  <b>{(tip.value || 0).toFixed(2)}</b>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
